import type { useTranslations } from 'next-intl'

/**
 * The timing vocabulary of a flow: the wait between two steps, the span of
 * the whole sequence, and the lines built from them. Day offsets are days
 * since the signup, one decimal.
 *
 * A reader gets it in their language from the `flows` catalog
 * (`describeStepGap`, `describeFlowCadence`), and a flow page's title and
 * description from the `seo` catalog (`flow-type-copy.ts`, by `flowSpan`).
 * The English `format*` helpers are for text a model, an index or a crawler
 * reads: the remix agent's prompt (`formatGapDistance`), the flow search
 * index (`formatFlowCardSubtitle`) and each flow's English Markdown twin.
 * Their wording never follows the UI copy.
 */

/** `useTranslations('flows')`, or `getTranslations('flows')` on the server. */
export type FlowsTranslator = ReturnType<typeof useTranslations<'flows'>>

const DAYS_PER_MONTH = 30
const MONTH_THRESHOLD_DAYS = 60
const HOURS_PER_DAY = 24

type FlowSpan = { unit: 'sameDay' } | { unit: 'days' | 'months'; count: number }

/** A span in days below two months, in whole months from there. */
export function flowSpan(spanDays: number): FlowSpan {
  if (spanDays <= 0) {
    return { unit: 'sameDay' }
  }
  if (spanDays >= MONTH_THRESHOLD_DAYS) {
    return { unit: 'months', count: Math.round(spanDays / DAYS_PER_MONTH) }
  }
  return { unit: 'days', count: spanDays }
}

/**
 * The distance between two emails, either way round, in whole days and
 * hours; zero and zero is the same day.
 *
 * Offsets carry one decimal, so their difference is both fractional and
 * float-noisy (6.2 - 1.1 = 5.100000000000001). Rounding the delta to whole
 * hours first is what keeps that off the stage: every label is a whole
 * number of days and hours, never a decimal.
 */
function gapDistance(
  fromDayOffset: number,
  toDayOffset: number
): { days: number; hours: number } {
  const delta = Math.abs(toDayOffset - fromDayOffset)
  if (delta <= 0) {
    return { days: 0, hours: 0 }
  }
  // A gap under half an hour still happened: floor it at one hour rather
  // than rounding it away into "same day".
  const totalHours = Math.max(1, Math.round(delta * HOURS_PER_DAY))
  return {
    days: Math.floor(totalHours / HOURS_PER_DAY),
    hours: totalHours % HOURS_PER_DAY,
  }
}

function englishCount(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/** "14 days" / "3 months" / "same day", in English. */
export function formatFlowSpan(spanDays: number): string {
  const span = flowSpan(spanDays)
  if (span.unit === 'sameDay') {
    return 'same day'
  }
  return span.unit === 'months'
    ? englishCount(span.count, 'month', 'months')
    : englishCount(span.count, 'day', 'days')
}

/** "5 emails", in English. */
export function formatEmailCount(emailCount: number): string {
  return englishCount(emailCount, 'email', 'emails')
}

/**
 * The distance between two emails in English, in whole units: "same day" /
 * "3 hours" / "1 day" / "2 days 12 hours".
 */
export function formatGapDistance(
  fromDayOffset: number,
  toDayOffset: number
): string {
  const { days, hours } = gapDistance(fromDayOffset, toDayOffset)
  if (days === 0 && hours === 0) {
    return 'same day'
  }
  if (days === 0) {
    return englishCount(hours, 'hour', 'hours')
  }
  const dayPart = englishCount(days, 'day', 'days')
  return hours === 0
    ? dayPart
    : `${dayPart} ${englishCount(hours, 'hour', 'hours')}`
}

/** The card line in English: "5 emails in 14 days" / "3 emails, same day". */
export function formatFlowCardSubtitle(args: {
  emailCount: number
  spanDays: number
}): string {
  const emails = formatEmailCount(args.emailCount)
  return args.spanDays > 0
    ? `${emails} in ${formatFlowSpan(args.spanDays)}`
    : `${emails}, same day`
}

/**
 * The label over a neighbouring email on the stage, relative to the one in
 * focus, in the reader's language: "2 days later" over the next one, "2 days
 * before" over the previous one, "same day" for either.
 */
export function describeStepGap(
  t: FlowsTranslator,
  focusDayOffset: number,
  neighbourDayOffset: number,
  direction: 'later' | 'before'
): string {
  const { days, hours } = gapDistance(focusDayOffset, neighbourDayOffset)
  if (days === 0 && hours === 0) {
    return t('stepGap.sameDay')
  }
  if (days === 0) {
    return t(`stepGap.${direction}.hours`, { hours })
  }
  return hours === 0
    ? t(`stepGap.${direction}.days`, { days })
    : t(`stepGap.${direction}.daysAndHours`, { days, hours })
}

/**
 * A flow's cadence in the reader's language, the line under its brand on a
 * card: "5 emails in 14 days" / "3 emails, same day".
 */
export function describeFlowCadence(
  t: FlowsTranslator,
  { emailCount, spanDays }: { emailCount: number; spanDays: number }
): string {
  const span = flowSpan(spanDays)
  if (span.unit === 'sameDay') {
    return t('cadence.sameDay', { emailCount })
  }
  return t(`cadence.${span.unit}`, { emailCount, spanCount: span.count })
}
